import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

// FUNNEL-INTEGRITY — the ONLY writer of canonical Patient residence
// (POST /api/surface/residence → persons.residence_country). Residence ≠ origin/nationality.
export const useCanonicalResidence = () => {
  const [state, setState] = useState({ loading: true, declared: null, residence_country: null });
  const refresh = React.useCallback(async () => {
    try {
      const { data } = await api.get('/surface/residence');
      setState({ loading: false, declared: !!data?.declared, residence_country: data?.residence_country || null });
    } catch {
      setState({ loading: false, declared: null, residence_country: null });
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { ...state, refresh };
};

export const ResidenceForm = ({ onDeclared, onCancel, compact = false, initialValue = '', writer = null, copy = null }) => {
  const { t } = useTranslation();
  const [countries, setCountries] = useState([]);
  const [value, setValue] = useState(initialValue || '');
  useEffect(() => { setValue(initialValue || ''); }, [initialValue]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/public/catalog/countries').then(({ data }) => { if (data?.success) setCountries(data.countries || []); }).catch(() => {});
  }, []);

  const save = async () => {
    if (!value) return;
    setSaving(true); setError('');
    try {
      // PROVIDER-OWNED-RESIDENCE-01 — the patient writes through
      // POST /surface/residence; an authorized representative writes through the
      // lead-scoped endpoint. BOTH persist via the same canonical fill-once
      // `persons.residence_country` authority, so this stays one form.
      const { data } = writer
        ? { data: await writer(value) }
        : await api.post('/surface/residence', { residence_country: value });
      onDeclared && onDeclared(data?.residence_country || value, data);
    } catch {
      setError(t('residencePrompt.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="residence-form">
      {!compact && <p className="text-sm text-slate-600" data-testid="residence-form-body">{t('residencePrompt.body')}</p>}
      <label className="block text-xs font-medium text-slate-700">{copy?.label || t('residencePrompt.label')}</label>
      <select
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="w-full p-2.5 rounded-lg border-2 border-slate-200 focus:border-emerald-600 focus:outline-none text-sm bg-white"
        data-testid="residence-select"
      >
        <option value="">{copy?.placeholder || t('residencePrompt.placeholder')}</option>
        {countries.map((c) => (
          <option key={c.code || c.name || c} value={c.code || c.name || c}>{c.name || c}</option>
        ))}
      </select>
      {(copy?.hint || t('residencePrompt.hint')) && <p className="text-[11px] text-slate-500">{copy?.hint || t('residencePrompt.hint')}</p>}
      {error && <p className="text-xs text-red-600" data-testid="residence-error">{error}</p>}
      <div className="flex gap-2 justify-end">
        {onCancel && <Button type="button" variant="ghost" size="sm" onClick={onCancel} data-testid="residence-cancel-btn">{t('residencePrompt.cancel')}</Button>}
        <Button type="button" size="sm" onClick={save} disabled={!value || saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="residence-save-btn">
          {saving ? t('residencePrompt.saving') : (copy?.save || t('residencePrompt.save'))}
        </Button>
      </div>
    </div>
  );
};

export const ResidencePromptDialog = ({ open, onOpenChange, onDeclared, initialValue = '' }) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="residence-prompt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MapPin className="h-4 w-4 text-emerald-600" />{t('residencePrompt.title')}</DialogTitle>
          <DialogDescription>{t('residencePrompt.body')}</DialogDescription>
        </DialogHeader>
        <ResidenceForm compact initialValue={initialValue} onDeclared={onDeclared} onCancel={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
};

// Persistent view/edit surface for existing Patients (dashboard/profile). Same canonical writer.
export const ResidenceCard = ({ residence }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [countries, setCountries] = useState([]);
  useEffect(() => {
    api.get('/public/catalog/countries').then(({ data }) => { if (data?.success) setCountries(data.countries || []); }).catch(() => {});
  }, []);
  if (residence.loading) return null;
  const code = residence.residence_country;
  const name = countries.find((c) => c.code === code || c.name === code)?.name || code;
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-3" data-testid="residence-card">
      <MapPin className="h-4 w-4 text-emerald-600 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-slate-500">{t('residencePrompt.cardTitle')}</p>
        <p className="text-sm font-semibold text-slate-900 truncate" data-testid="residence-card-value">
          {residence.declared ? name : t('residencePrompt.notDeclared')}
        </p>
      </div>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="residence-card-edit-btn">
        {residence.declared ? t('residencePrompt.edit') : t('residencePrompt.bannerCta')}
      </Button>
      <ResidencePromptDialog open={open} onOpenChange={setOpen} initialValue={code || ''}
        onDeclared={() => { setOpen(false); residence.refresh(); }} />
    </div>
  );
};

export const ResidenceBanner = ({ onDeclared }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="residence-banner">
      <MapPin className="h-5 w-5 text-amber-700 flex-shrink-0" />
      <div className="flex-1">
        <p className="text-sm font-semibold text-amber-900">{t('residencePrompt.bannerTitle')}</p>
        <p className="text-xs text-amber-800">{t('residencePrompt.bannerBody')}</p>
      </div>
      <Button size="sm" onClick={() => setOpen(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="residence-banner-cta">{t('residencePrompt.bannerCta')}</Button>
      <ResidencePromptDialog open={open} onOpenChange={setOpen} onDeclared={(v) => { setOpen(false); onDeclared && onDeclared(v); }} />
    </div>
  );
};
